import { ThemeProps } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/themeSchema';
import { ThemeProperties } from '@mutiny-pkg/champaign-schemas/src/schemas/themeProperties/types';
import { useEffect, useRef } from 'react';

const injectedFontFaceKeys = new Set<string>();

const escapeCssString = (value: string) => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const injectFontFaceStyle = (fontFamily: string, fontWeight: string, src: string, style: string) => {
  const key = `${fontFamily}|${fontWeight}|${style}|${src}`;
  if (injectedFontFaceKeys.has(key)) {
    return;
  }
  try {
    // eslint-disable-next-line no-new
    new URL(src, window.location.href);
  } catch {
    return;
  }
  const el = document.createElement('style');
  el.setAttribute('data-mtn-font', '');
  el.textContent = `@font-face{font-family:"${escapeCssString(
    fontFamily,
  )}";font-weight:${fontWeight};font-style:${style};src:url("${escapeCssString(src)}");}`;
  document.head.appendChild(el);
  injectedFontFaceKeys.add(key);
};

export const loadFontFace = async (fontFamily: string, fontWeight: string, src: string, style?: string) => {
  if (!fontFamily || !fontWeight || !src) {
    return;
  }

  const fontStyle = style || 'normal';
  injectFontFaceStyle(fontFamily, fontWeight, src, fontStyle);

  const fontFace = new FontFace(fontFamily, `url(${src})`, { weight: fontWeight, style: fontStyle });
  document.fonts.add(fontFace);
  try {
    await fontFace.load();
  } catch (error) {
    console.warn('Error loading font face', error);
  }
};

export function useFonts(fontFaces: ThemeProperties['fontFaces'] | ThemeProps['fonts'], dynamicFonts: boolean) {
  const loadedFontsRef = useRef<ThemeProperties['fontFaces']>([]);

  useEffect(() => {
    if (!dynamicFonts) {
      return;
    }

    fontFaces?.forEach((font) => {
      // Old schema font face
      if ('src' in font) {
        if (loadedFontsRef.current.includes(font)) {
          return;
        }
        loadFontFace(font.fontFamily, font.fontWeight, font.src, font.fontStyle);
        loadedFontsRef.current.push(font);
      }

      // New schema font face
      if ('variants' in font) {
        font.variants?.forEach((variant) => {
          const fontFace = {
            fontFamily: font.family,
            fontWeight: variant.weight,
            src: variant.url,
            fontStyle: variant.style,
          };
          if (loadedFontsRef.current.includes(fontFace)) {
            return;
          }
          loadFontFace(fontFace.fontFamily, fontFace.fontWeight, fontFace.src, fontFace.fontStyle);
          loadedFontsRef.current.push(fontFace);
        });
      }
    });
  }, [fontFaces, dynamicFonts]);
}
